import { useState, useEffect } from 'react'
import { useMonthlyReports, useCreateMonthlyReport, useUpdateMonthlyReport } from '@/features/monthly-reports/hooks/useMonthlyReports'
import { useClients } from '@/features/clients/hooks/useClients'
import { usePayments } from '@/features/payments/hooks/usePayments'
import { useInvoices } from '@/features/invoices/hooks/useInvoices'
import { getMonthStart, getMonthEnd, getCurrentMonthYear } from '@/lib/date-strings'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { DataTable } from '@/components/data-table/DataTable'
import { FileText, Plus, Download } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export function ReportsPage() {
  const { data: reports } = useMonthlyReports()
  const { data: clients } = useClients()
  const { data: payments } = usePayments()
  const { data: invoices } = useInvoices()
  const createReport = useCreateMonthlyReport()
  const updateReport = useUpdateMonthlyReport()
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [generating, setGenerating] = useState(false)

  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const { year: currentYear, month: currentMonth } = getCurrentMonthYear()
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString())
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth.toString())
  const [emailAddresses, setEmailAddresses] = useState('')

  // Auto-populate email when client is selected
  useEffect(() => {
    if (selectedClientId) {
      const client = clients?.find(c => c.id === selectedClientId)
      if (client?.email) {
        setEmailAddresses(client.email)
      } else {
        setEmailAddresses('')
      }
    }
  }, [selectedClientId, clients])

  const generatePDF = (reportData: any) => {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    
    // Header
    doc.setFontSize(20)
    doc.text('Monthly Collection Report', pageWidth / 2, 20, { align: 'center' })
    
    doc.setFontSize(12)
    doc.text(`Client: ${reportData.clientName}`, 14, 35)
    doc.text(`Report Period: ${reportData.monthYear}`, 14, 42)
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 49)
    
    let yPosition = 60

    // Collections Summary
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Collections Summary', 14, yPosition)
    yPosition += 5

    autoTable(doc, {
      startY: yPosition,
      head: [['Description', 'Amount']],
      body: [
        ['Total Collected', formatCurrency(reportData.totalCollected)],
        ['Baseline AR Collected', formatCurrency(reportData.baselineCollected)],
        ['New Past-Due Collected', formatCurrency(reportData.newPastDueCollected)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
    })

    yPosition = (doc as any).lastAutoTable.finalY + 15

    // Payment Details
    if (reportData.payments.length > 0) {
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Payment Details', 14, yPosition)
      yPosition += 5

      const paymentRows = reportData.payments.map((p: any) => [
        formatDate(p.payment_date),
        p.customerName,
        formatCurrency(p.amount),
        p.invoiceNumbers || '—',
        p.payment_method.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' '),
      ])

      autoTable(doc, {
        startY: yPosition,
        head: [['Date', 'Customer', 'Amount', 'Invoice', 'Method']],
        body: paymentRows,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
      })

      yPosition = (doc as any).lastAutoTable.finalY + 15
    }

    // AR Summary
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('AR Summary', 14, yPosition)
    yPosition += 5

    autoTable(doc, {
      startY: yPosition,
      head: [['Description', 'Amount']],
      body: [
        ['Beginning AR', formatCurrency(reportData.beginningAR)],
        ['New AR Added', formatCurrency(reportData.newARAdded)],
        ['Collections', formatCurrency(reportData.totalCollected)],
        ['Ending AR', formatCurrency(reportData.endingAR)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
    })

    yPosition = (doc as any).lastAutoTable.finalY + 15

    // Fees Summary
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Fees Owed to HeartWest Advisors', 14, yPosition)
    yPosition += 5

    autoTable(doc, {
      startY: yPosition,
      head: [['Description', 'Amount']],
      body: [
        ['Collection Fee (8% of eligible payments)', formatCurrency(reportData.total_fee)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94] },
      footStyles: { fillColor: [34, 197, 94], fontStyle: 'bold' },
    })

    return doc
  }

  const handleGenerateReport = async () => {
    if (!selectedClientId) {
      alert('Please select a client')
      return
    }

    setGenerating(true)

    try {
      const client = clients?.find(c => c.id === selectedClientId)
      if (!client) throw new Error('Client not found')

      const year = parseInt(selectedYear)
      const month = parseInt(selectedMonth)
      const reportMonth = `${year}-${String(month).padStart(2, '0')}-01`
      const monthYear = new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })

      // Get payments for this client and month
      // Include ALL payments regardless of source (manual, csv, qbo)
      // Exclude only pre-contract payments (is_pre_contract = true)
      // Use string comparison to avoid timezone bugs (e.g., 2026-11-01 in local time)
      const monthStart = getMonthStart(year, month)
      const monthEnd = getMonthEnd(year, month)

      const clientPayments = payments?.filter(p => {
        const matches = (
          p.client_id === selectedClientId &&
          p.payment_date >= monthStart &&
          p.payment_date <= monthEnd &&
          p.is_pre_contract === false // Explicitly check false (not just !p.is_pre_contract)
        )
        return matches
      }) || []

      // Debug logging for troubleshooting
      console.log('Report generation for', client.name, monthYear)
      console.log('Total payments in system:', payments?.length || 0)
      console.log('Filtered client payments for month:', clientPayments.length)
      console.log('Month range:', monthStart, 'to', monthEnd)
      if (clientPayments.length === 0 && payments) {
        // Log potential issues if no payments found
        const clientAllPayments = payments.filter(p => p.client_id === selectedClientId)
        const monthPayments = payments.filter(p => p.payment_date >= monthStart && p.payment_date <= monthEnd)
        console.log('All payments for this client:', clientAllPayments.length)
        console.log('All payments for this month (any client):', monthPayments.length)
        console.log('Client payments breakdown by source:', {
          qbo: clientAllPayments.filter(p => p.source === 'qbo').length,
          manual: clientAllPayments.filter(p => p.source === 'manual').length,
          csv: clientAllPayments.filter(p => p.source === 'csv').length,
        })
      }

      // Calculate totals
      let totalCollected = 0
      let baselineCollected = 0
      let newPastDueCollected = 0
      let total_fee = 0

      for (const payment of clientPayments) {
        totalCollected += payment.amount

        if (payment.applied_to_baseline) {
          baselineCollected += payment.amount
        } else {
          newPastDueCollected += payment.amount
        }

        // Use stored collection_fee_owed (never recalculate)
        // Pre-contract payments have collection_fee_owed = 0
        total_fee += payment.collection_fee_owed
      }

      const baseline_fee_8 = 0 // Deprecated: no longer using tiered fees
      const baseline_fee_12 = 0 // Deprecated: no longer using tiered fees
      const new_past_due_fee = 0 // Deprecated: no longer using tiered fees

      // Calculate AR metrics
      const clientInvoices = invoices?.filter(inv => inv.client_id === selectedClientId) || []
      const endingAR = clientInvoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0)
      const beginningAR = endingAR + totalCollected
      // Use string comparison for invoice dates to avoid timezone bugs
      const newARAdded = clientInvoices.filter(inv =>
        inv.invoice_date >= monthStart && inv.invoice_date <= monthEnd
      ).reduce((sum, inv) => sum + inv.original_amount, 0)

      // Generate PDF
      const reportData = {
        clientName: client.name,
        monthYear,
        totalCollected,
        baselineCollected,
        newPastDueCollected,
        baseline_fee_8,
        baseline_fee_12,
        new_past_due_fee,
        total_fee,
        beginningAR,
        newARAdded,
        endingAR,
        payments: clientPayments.map(p => {
          // Get invoice numbers for this payment
          // QBO payments may use invoice_id (single) or invoice_ids (array)
          // Manual payments may use invoice_ids (array)
          const invoiceIdList: string[] = []

          // Add single invoice_id if present (QBO style)
          if (p.invoice_id) {
            invoiceIdList.push(p.invoice_id)
          }

          // Add all invoice_ids from array (both QBO and manual)
          if (p.invoice_ids && p.invoice_ids.length > 0) {
            invoiceIdList.push(...p.invoice_ids)
          }

          // Remove duplicates (in case both invoice_id and invoice_ids have same ID)
          const uniqueInvoiceIds = [...new Set(invoiceIdList)]

          const invoiceNumbers = uniqueInvoiceIds
            .map(invId => {
              const invoice = invoices?.find(inv => inv.id === invId)
              return invoice?.invoice_number
            })
            .filter(Boolean)
            .join(', ')

          return {
            payment_date: p.payment_date,
            customerName: (p as any).customer?.name || 'Unknown',
            amount: p.amount,
            invoiceNumbers: invoiceNumbers || '—',
            payment_method: p.payment_method,
            applied_to_baseline: p.applied_to_baseline,
          }
        }),
      }

      const pdf = generatePDF(reportData)
      const pdfBlob = pdf.output('blob')
      const pdfDataUrl = pdf.output('dataurlstring')

      // Create the report record
      const createdReport = await createReport.mutateAsync({
        client_id: selectedClientId,
        report_month: reportMonth,
        total_collected: totalCollected,
        baseline_collected: baselineCollected,
        new_past_due_collected: newPastDueCollected,
        total_fee: total_fee,
        baseline_fee_8: baseline_fee_8,
        baseline_fee_12: baseline_fee_12,
        new_past_due_fee: new_past_due_fee,
        sent_to: emailAddresses || null,
        pdf_data: pdfDataUrl,
      } as any)

      // Download PDF
      const link = document.createElement('a')
      link.href = URL.createObjectURL(pdfBlob)
      link.download = `${client.name}-Report-${monthYear.replace(' ', '-')}.pdf`
      link.click()

      // Send email if provided
      if (emailAddresses && emailAddresses.trim()) {
        const emails = emailAddresses.split(',').map(e => e.trim()).filter(e => e)
        // In a real app, this would call an API endpoint to send the email
        // For now, we'll just update the sent_to field
        await updateReport.mutateAsync({
          id: (createdReport as any).id,
          updates: {
            sent_to: emails.join(', '),
            sent_at: new Date().toISOString(),
          },
        })
        alert(`Report generated and will be sent to: ${emails.join(', ')}`)
      } else {
        alert('Report generated successfully!')
      }

      setShowGenerateModal(false)
      setSelectedClientId('')
      setEmailAddresses('')
    } catch (error: any) {
      console.error('Error generating report:', error)
      const errorMessage = error?.message || error?.error?.message || JSON.stringify(error)
      alert(`Error generating report: ${errorMessage}`)
    } finally {
      setGenerating(false)
    }
  }

  const handleDownloadReport = (report: any) => {
    if (report.pdf_data) {
      // Convert data URL back to blob and download
      fetch(report.pdf_data)
        .then(res => res.blob())
        .then(blob => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          const [year, month] = report.report_month.split('-').map(Number)
          const monthYear = new Date(year, month - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
          link.download = `${report.client?.name}-Report-${monthYear.replace(' ', '-')}.pdf`
          link.click()
        })
    } else {
      // Regenerate PDF from report data if pdf_data is not stored
      const client = clients?.find(c => c.id === report.client_id)
      if (!client) return

      const [year, month] = report.report_month.split('-').map(Number)
      const monthYear = new Date(year, month - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      const reportData = {
        clientName: client.name,
        monthYear,
        totalCollected: report.total_collected,
        baselineCollected: report.baseline_collected,
        newPastDueCollected: report.new_past_due_collected,
        baseline_fee_8: report.baseline_fee_8,
        baseline_fee_12: report.baseline_fee_12,
        new_past_due_fee: report.new_past_due_fee,
        total_fee: report.total_fee,
        beginningAR: 0, // Would need to recalculate
        newARAdded: 0, // Would need to recalculate
        endingAR: 0, // Would need to recalculate
        payments: [],
      }

      const pdf = generatePDF(reportData)
      pdf.save(`${client.name}-Report-${monthYear.replace(' ', '-')}.pdf`)
    }
  }

  const columns = [
    {
      key: 'client',
      header: 'Client',
      render: (r: any) => r.client?.name || '—',
    },
    {
      key: 'report_month',
      header: 'Month',
      render: (r: any) => {
        // Parse YYYY-MM-DD format directly to avoid timezone issues
        const [year, month] = r.report_month.split('-').map(Number)
        return new Date(year, month - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      },
    },
    {
      key: 'total_collected',
      header: 'Total Collected',
      render: (r: any) => formatCurrency(r.total_collected),
    },
    {
      key: 'total_fee',
      header: 'Total Fee',
      render: (r: any) => formatCurrency(r.total_fee),
    },
    {
      key: 'sent_status',
      header: 'Status',
      render: (r: any) => r.sent_at ? (
        <Badge variant="success">Sent</Badge>
      ) : (
        <Badge variant="default">Not Sent</Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r: any) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleDownloadReport(r)}
        >
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      ),
    },
  ]

  const thisYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: (thisYear - i).toString(),
    label: (thisYear - i).toString(),
  }))

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' }),
  }))

  const clientOptions = clients?.map(c => ({ value: c.id, label: c.name })) || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monthly Reports</h1>
          <p className="text-sm text-gray-600">Generate and manage collection reports</p>
        </div>
        <Button onClick={() => setShowGenerateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </div>

      <Card>
        <DataTable
          data={reports || []}
          columns={columns}
          emptyMessage="No reports yet. Generate your first report to get started."
        />
      </Card>

      <Modal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        title="Generate Monthly Report"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Client"
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            options={clientOptions}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              options={yearOptions}
              required
            />
            <Select
              label="Month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              options={monthOptions}
              required
            />
          </div>

          <div>
            <Input
              label="Email Address (Optional)"
              type="text"
              value={emailAddresses}
              onChange={(e) => setEmailAddresses(e.target.value)}
              placeholder="email@example.com, another@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter one or more email addresses separated by commas. Leave blank to skip email delivery.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowGenerateModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateReport}
              loading={generating}
              disabled={!selectedClientId}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
